import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileInvoice,
  faPaperclip,
  faPaperPlane,
  faPenToSquare,
  faCircleCheck,
  faCircleXmark,
  faRotate,
  faHourglassHalf,
  faArrowUpRightFromSquare,
} from '@fortawesome/free-solid-svg-icons';
import {
  authorizationQuotationService,
  QUOTATION_DOCUMENT_ACCEPT,
  QUOTATION_REVISION_STATUS_LABELS,
  type AuthorizationQuotationRevision,
  type AuthorizationQuotationRevisionStatus,
  type AuthorizationQuotationThread,
} from '@/services/authorization-quotation.service';
import {
  COMMUNICATION_METHOD_LABELS,
  type AuthorizationCommunicationMethod,
} from '@/services/authorization-case.service';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast, swalConfirm } from '@/lib/swal';

/**
 * Phase 7 Slice 5B — the request-scoped quotation thread of ONE authorization
 * case (docs/AUTHORIZATION_REQUEST_SCOPED_QUOTATION_DESIGN.md §D, §E, §J;
 * D-127 OD-2, OD-3′, OD-5, OD-6; D-128 Q-1, Q-2, Q-3, Q-4, Q-7).
 *
 * What this panel is
 *   • Rendered only for cases whose request kind carries a quotation
 *     (`thread.requiresQuotation`) — for every other kind it renders nothing and
 *     the Slice 1 "Send to Customer" panel remains the only customer step.
 *   • Read-only for everyone who can read the case; the Commercial controls are
 *     offered only when the page's `canAct` mirrors the server (record-communication
 *     permission, `currentOwner = COMMERCIAL`, claim-first / assignee-only, Owner
 *     wildcard, PENDING). The server remains authoritative — this only avoids
 *     offering an action it would refuse.
 *   • Every state is the SERVER's: `threadStatus`, `closed` and `currentRevision`
 *     come from the thread DTO and nothing is re-derived here.
 *
 * What it deliberately does not do
 *   • It never resolves the case: after ACCEPT / REJECT the next step is the existing
 *     Decision panel (recommend to OPS / Return to OPS) — no quotation action here.
 *   • It offers no revision after ACCEPT / REJECT (Q-1) and no withdraw (Q-7).
 *   • It never shows a storage URL to a customer: `documentUrl` is the staff-only AME
 *     signed URL and this panel is the staff plane.
 *   • It shares nothing with the job-level quotation on the Inspection.
 */

export interface QuotationPanelContact {
  id: string;
  firstName: string;
  lastName: string;
}

interface QuotationPanelProps {
  authorizationId: string;
  thread: AuthorizationQuotationThread | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  /** The page's mirror of the server guard for Commercial quotation actions. */
  canAct: boolean;
  contacts: QuotationPanelContact[] | undefined;
  /** Invalidate the page's case / journey / quotation queries after a write. */
  onChanged: () => void;
}

const SELECT_CLASS =
  'flex h-10 w-full rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 disabled:opacity-50';

const STATUS_TONE: Record<AuthorizationQuotationRevisionStatus, string> = {
  DRAFT: 'bg-muted text-muted-foreground',
  SENT: 'bg-sky-500/10 text-sky-700',
  ACCEPTED: 'bg-emerald-500/10 text-emerald-700',
  REJECTED: 'bg-red-500/10 text-red-700',
  MODIFICATION_REQUESTED: 'bg-amber-500/10 text-amber-700',
};

const STATUS_ICON = {
  DRAFT: faPenToSquare,
  SENT: faHourglassHalf,
  ACCEPTED: faCircleCheck,
  REJECTED: faCircleXmark,
  MODIFICATION_REQUESTED: faRotate,
} as const;

function extractMsg(error: Error, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as { error?: { message?: string } } | undefined;
    if (body?.error?.message) return body.error.message;
  }
  return fallback;
}

function fmt(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString() : '—';
}

function StatusBadge({ status }: { status: AuthorizationQuotationRevisionStatus }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_TONE[status]}`}
    >
      <FontAwesomeIcon icon={STATUS_ICON[status]} className="h-3 w-3" />
      {QUOTATION_REVISION_STATUS_LABELS[status]}
    </span>
  );
}

/** One row of the append-only revision history. Superseded revisions stay visible. */
function RevisionRow({
  revision,
  isCurrent,
  revisions,
}: {
  revision: AuthorizationQuotationRevision;
  isCurrent: boolean;
  revisions: AuthorizationQuotationRevision[];
}) {
  const supersededBy = revision.supersededByRevisionId
    ? revisions.find((r) => r.id === revision.supersededByRevisionId)
    : undefined;
  return (
    <li
      data-testid={`quotation-revision-${revision.revisionNumber}`}
      className={`rounded-lg border px-3 py-2 text-sm ${
        isCurrent ? 'border-primary/40 bg-primary/5' : 'border-border bg-card'
      }`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-foreground">Revision {revision.revisionNumber}</span>
        {isCurrent && (
          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
            Current
          </span>
        )}
        <StatusBadge status={revision.status} />
      </div>
      <dl className="mt-1 grid grid-cols-1 gap-x-4 gap-y-0.5 text-xs text-muted-foreground sm:grid-cols-2">
        <div>
          <dt className="inline font-medium">Reference: </dt>
          <dd className="inline text-foreground">{revision.quotationReference ?? '—'}</dd>
        </div>
        <div>
          <dt className="inline font-medium">Document: </dt>
          <dd className="inline text-foreground">
            {revision.documentName ? (
              revision.documentUrl ? (
                <a
                  href={revision.documentUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary underline decoration-dotted"
                >
                  {revision.documentName}
                  <FontAwesomeIcon
                    icon={faArrowUpRightFromSquare}
                    className="ml-1 inline h-2.5 w-2.5"
                  />
                </a>
              ) : (
                revision.documentName
              )
            ) : (
              '—'
            )}
          </dd>
        </div>
        <div>
          <dt className="inline font-medium">Prepared: </dt>
          <dd className="inline">
            {revision.preparedByName}, {fmt(revision.preparedAt)}
          </dd>
        </div>
        <div>
          <dt className="inline font-medium">Sent: </dt>
          <dd className="inline">{fmt(revision.sentAt)}</dd>
        </div>
        {revision.decidedAt && (
          <div>
            <dt className="inline font-medium">Customer decided: </dt>
            <dd className="inline">{fmt(revision.decidedAt)}</dd>
          </div>
        )}
        {supersededBy && (
          <div>
            <dt className="inline font-medium">Superseded by: </dt>
            <dd className="inline">Revision {supersededBy.revisionNumber}</dd>
          </div>
        )}
      </dl>
      {revision.revisionReason && (
        <p className="mt-1 whitespace-pre-wrap text-xs text-foreground">
          {revision.revisionReason}
        </p>
      )}
    </li>
  );
}

export function QuotationPanel({
  authorizationId,
  thread,
  isLoading,
  isError,
  onRetry,
  canAct,
  contacts,
  onChanged,
}: QuotationPanelProps) {
  const queryClient = useQueryClient();
  const [reference, setReference] = useState('');
  const [referenceDirty, setReferenceDirty] = useState(false);
  const [sendMethod, setSendMethod] = useState<AuthorizationCommunicationMethod>('PORTAL');
  const [sendContactId, setSendContactId] = useState('');
  const [sendSummary, setSendSummary] = useState('');
  const [reviseNote, setReviseNote] = useState('');
  const [reviseReference, setReviseReference] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const applyThread = (next: AuthorizationQuotationThread) => {
    queryClient.setQueryData(['authorization-quotation', authorizationId], next);
    onChanged();
  };

  const prepareMutation = useMutation({
    mutationFn: () =>
      authorizationQuotationService.prepare(authorizationId, {
        quotationReference: reference.trim() || undefined,
      }),
    onSuccess: (next) => {
      toast.success('Quotation revision 1 prepared as a draft');
      setReference('');
      setReferenceDirty(false);
      applyThread(next);
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to prepare the quotation')),
  });

  const updateMutation = useMutation({
    mutationFn: (revisionId: string) =>
      authorizationQuotationService.updateDraft(authorizationId, revisionId, {
        quotationReference: reference.trim() === '' ? null : reference.trim(),
      }),
    onSuccess: (next) => {
      toast.success('Quotation reference saved');
      setReferenceDirty(false);
      applyThread(next);
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to save the reference')),
  });

  const uploadMutation = useMutation({
    mutationFn: ({ revisionId, file }: { revisionId: string; file: File }) =>
      authorizationQuotationService.uploadDocument(authorizationId, revisionId, file),
    onSuccess: (next) => {
      toast.success('Quotation document attached');
      if (fileInputRef.current) fileInputRef.current.value = '';
      applyThread(next);
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to attach the document')),
  });

  const sendMutation = useMutation({
    mutationFn: (revisionId: string) =>
      authorizationQuotationService.send(authorizationId, revisionId, {
        method: sendMethod,
        customerContactId: sendContactId || undefined,
        summary: sendSummary.trim(),
      }),
    onSuccess: (next) => {
      toast.success(
        'Quotation sent to customer — the case stays with your department while they decide',
      );
      setSendSummary('');
      applyThread(next);
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to send the quotation')),
  });

  const reviseMutation = useMutation({
    mutationFn: (revisionId: string) =>
      authorizationQuotationService.revise(authorizationId, revisionId, {
        note: reviseNote.trim() || undefined,
        quotationReference: reviseReference.trim() || undefined,
      }),
    onSuccess: (next) => {
      toast.success(`Revision ${next.currentRevision?.revisionNumber ?? ''} opened as a draft`);
      setReviseNote('');
      setReviseReference('');
      setReference('');
      setReferenceDirty(false);
      applyThread(next);
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to revise the quotation')),
  });

  const busy =
    prepareMutation.isPending ||
    updateMutation.isPending ||
    uploadMutation.isPending ||
    sendMutation.isPending ||
    reviseMutation.isPending;

  // ── Loading / error / not-a-quotation-kind ────────────────────────────────
  if (isLoading && !thread) {
    return (
      <div data-testid="quotation-panel" className="rounded-xl border border-border bg-card p-5">
        <h3 className="text-sm font-semibold text-foreground">Quotation</h3>
        <p className="mt-2 text-sm text-muted-foreground">Loading quotation…</p>
      </div>
    );
  }
  if (isError && !thread) {
    return (
      <div data-testid="quotation-panel" className="rounded-xl border border-border bg-card p-5">
        <h3 className="text-sm font-semibold text-foreground">Quotation</h3>
        <p className="mt-2 text-sm text-muted-foreground">The quotation could not be loaded.</p>
        <Button variant="secondary" size="sm" className="mt-3" onClick={onRetry}>
          Retry
        </Button>
      </div>
    );
  }
  if (!thread || !thread.requiresQuotation) return null;

  const current = thread.currentRevision;
  const currentStatus = thread.threadStatus;
  const hasContent =
    !!current && (current.quotationReference !== null || current.documentAssetId !== null);
  const isResend = !!current && current.revisionNumber > 1;
  const referenceValue = referenceDirty ? reference : (current?.quotationReference ?? '');

  // ── Situation text — the server's state, in the frozen vocabulary ─────────
  let situation: string;
  let situationTone = 'bg-muted text-muted-foreground';
  switch (currentStatus) {
    case 'NONE':
      situation = 'No quotation has been prepared for this request yet.';
      break;
    case 'DRAFT':
      situation = `Revision ${current!.revisionNumber} is a draft. ${
        hasContent
          ? 'It carries quotation content and can be sent to the customer.'
          : 'Add a quotation reference or attach the document before sending — an empty quotation cannot be sent.'
      }`;
      break;
    case 'SENT':
      situation = `Revision ${current!.revisionNumber} was sent to the customer on ${fmt(
        current!.sentAt,
      )}. Awaiting the customer's decision — the case stays with Commercial.`;
      situationTone = 'bg-sky-500/10 text-sky-800';
      break;
    case 'MODIFICATION_REQUESTED':
      situation = `The customer requested a modification to revision ${
        current!.revisionNumber
      }. Revise the quotation, then send the new revision.`;
      situationTone = 'bg-amber-500/10 text-amber-800';
      break;
    case 'ACCEPTED':
      situation = `The customer accepted revision ${current!.revisionNumber} on ${fmt(
        current!.decidedAt,
      )}. This decision is final — the quotation is closed and no further revision can be prepared.`;
      situationTone = 'bg-emerald-500/10 text-emerald-800';
      break;
    case 'REJECTED':
      situation = `The customer rejected revision ${current!.revisionNumber} on ${fmt(
        current!.decidedAt,
      )}. This decision is final — the quotation is closed and no further revision can be prepared.`;
      situationTone = 'bg-red-500/10 text-red-800';
      break;
  }

  return (
    <div data-testid="quotation-panel" className="rounded-xl border border-border bg-card">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-5 py-3.5">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <FontAwesomeIcon icon={faFileInvoice} className="h-3.5 w-3.5 text-muted-foreground" />
          Quotation
        </h3>
        <span data-testid="quotation-thread-status" className="text-xs text-muted-foreground">
          {currentStatus === 'NONE' ? 'Not prepared' : <StatusBadge status={currentStatus} />}
        </span>
      </div>

      <div className="space-y-4 px-5 py-4">
        <p
          data-testid="quotation-situation"
          className={`rounded-lg px-3 py-2 text-sm ${situationTone}`}
        >
          {situation}
        </p>

        {thread.closed && canAct && (
          <p className="text-xs text-muted-foreground">
            Next step: record your recommendation or return the case to Operations from the Decision
            panel. Sending is closed for this quotation.
          </p>
        )}

        {/* ── Commercial controls — only where the server would accept them ── */}
        {canAct && currentStatus === 'NONE' && (
          <div className="space-y-2 rounded-lg border border-dashed border-border p-3">
            <label
              htmlFor="quotation-prepare-reference"
              className="block text-xs font-medium text-muted-foreground"
            >
              Quotation reference (optional now — required before sending unless a document is
              attached)
            </label>
            <Input
              id="quotation-prepare-reference"
              value={reference}
              onChange={(e) => {
                setReference(e.target.value);
                setReferenceDirty(true);
              }}
              placeholder="e.g. ERP quotation number"
              maxLength={255}
            />
            <Button
              onClick={() => prepareMutation.mutate()}
              disabled={busy}
              className="min-h-[40px]"
            >
              <FontAwesomeIcon icon={faPenToSquare} className="mr-2 h-3.5 w-3.5" />
              Prepare Quotation
            </Button>
          </div>
        )}

        {canAct && currentStatus === 'DRAFT' && current && (
          <div className="space-y-4 rounded-lg border border-dashed border-border p-3">
            <div className="space-y-2">
              <label
                htmlFor="quotation-draft-reference"
                className="block text-xs font-medium text-muted-foreground"
              >
                Quotation reference
              </label>
              <div className="flex gap-2">
                <Input
                  id="quotation-draft-reference"
                  value={referenceValue}
                  onChange={(e) => {
                    setReference(e.target.value);
                    setReferenceDirty(true);
                  }}
                  placeholder="e.g. ERP quotation number"
                  maxLength={255}
                />
                <Button
                  variant="secondary"
                  onClick={() => updateMutation.mutate(current.id)}
                  disabled={busy || !referenceDirty}
                  className="min-h-[40px] shrink-0"
                >
                  Save Reference
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <label
                htmlFor="quotation-document-file"
                className="block text-xs font-medium text-muted-foreground"
              >
                Quotation document (PDF, Word, Excel, JPEG or PNG — up to 25 MB)
              </label>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  id="quotation-document-file"
                  ref={fileInputRef}
                  type="file"
                  accept={QUOTATION_DOCUMENT_ACCEPT}
                  className="text-sm text-foreground file:mr-3 file:rounded-md file:border-0 file:bg-secondary file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-secondary-foreground"
                  disabled={busy}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) uploadMutation.mutate({ revisionId: current.id, file });
                  }}
                />
                {current.documentName && (
                  <span className="text-xs text-muted-foreground">
                    <FontAwesomeIcon icon={faPaperclip} className="mr-1 h-3 w-3" />
                    Attached: {current.documentName} (choosing a file replaces it)
                  </span>
                )}
              </div>
            </div>

            <div className="space-y-3 border-t border-border pt-3">
              <p className="text-xs text-muted-foreground">
                {isResend ? 'Resend' : 'Send'} revision {current.revisionNumber} to the customer.
                Sending keeps the case with your department — only the waiting state moves to the
                customer.
              </p>
              <div>
                <label
                  htmlFor="quotation-send-method"
                  className="mb-1 block text-xs font-medium text-muted-foreground"
                >
                  Method
                </label>
                <select
                  id="quotation-send-method"
                  value={sendMethod}
                  onChange={(e) =>
                    setSendMethod(e.target.value as AuthorizationCommunicationMethod)
                  }
                  className={SELECT_CLASS}
                >
                  {(
                    Object.keys(COMMUNICATION_METHOD_LABELS) as AuthorizationCommunicationMethod[]
                  ).map((m) => (
                    <option key={m} value={m}>
                      {COMMUNICATION_METHOD_LABELS[m]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  htmlFor="quotation-send-contact"
                  className="mb-1 block text-xs font-medium text-muted-foreground"
                >
                  Customer Contact
                </label>
                <select
                  id="quotation-send-contact"
                  value={sendContactId}
                  onChange={(e) => setSendContactId(e.target.value)}
                  className={SELECT_CLASS}
                >
                  <option value="">— Select contact —</option>
                  {contacts?.map((c) => (
                    <option key={c.id} value={c.id}>
                      {`${c.firstName} ${c.lastName}`.trim()}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  htmlFor="quotation-send-summary"
                  className="mb-1 block text-xs font-medium text-muted-foreground"
                >
                  Summary
                </label>
                <Textarea
                  id="quotation-send-summary"
                  value={sendSummary}
                  onChange={(e) => setSendSummary(e.target.value)}
                  placeholder="What was sent to the customer"
                  maxLength={2000}
                />
              </div>
              {!hasContent && (
                <p data-testid="quotation-empty-send-hint" className="text-xs text-amber-700">
                  Add a quotation reference or attach the document first — an empty quotation cannot
                  be sent.
                </p>
              )}
              <Button
                onClick={() => sendMutation.mutate(current.id)}
                disabled={busy || !hasContent || !sendSummary.trim()}
                className="min-h-[40px] w-full"
              >
                <FontAwesomeIcon icon={faPaperPlane} className="mr-2 h-3.5 w-3.5" />
                {isResend ? 'Resend to Customer' : 'Send to Customer'}
              </Button>
            </div>
          </div>
        )}

        {canAct && currentStatus === 'SENT' && (
          <p className="text-xs text-muted-foreground">
            Waiting for the customer. If they answered outside the portal, record their decision in
            the Send to Customer panel.
          </p>
        )}

        {canAct && currentStatus === 'MODIFICATION_REQUESTED' && current && (
          <div className="space-y-3 rounded-lg border border-dashed border-border p-3">
            <p className="text-xs text-muted-foreground">
              Revising opens revision {current.revisionNumber + 1} as a new draft. Revision{' '}
              {current.revisionNumber} stays in the history unchanged.
            </p>
            <div>
              <label
                htmlFor="quotation-revise-note"
                className="mb-1 block text-xs font-medium text-muted-foreground"
              >
                What changes (optional)
              </label>
              <Textarea
                id="quotation-revise-note"
                value={reviseNote}
                onChange={(e) => setReviseNote(e.target.value)}
                placeholder="Commercial note on the revision"
                maxLength={2000}
              />
            </div>
            <div>
              <label
                htmlFor="quotation-revise-reference"
                className="mb-1 block text-xs font-medium text-muted-foreground"
              >
                New quotation reference (optional)
              </label>
              <Input
                id="quotation-revise-reference"
                value={reviseReference}
                onChange={(e) => setReviseReference(e.target.value)}
                placeholder="e.g. ERP quotation number"
                maxLength={255}
              />
            </div>
            <Button
              onClick={async () => {
                const ok = await swalConfirm({
                  title: `Revise the quotation?`,
                  text: `This opens revision ${current.revisionNumber + 1} as a draft; revision ${current.revisionNumber} remains in the history.`,
                  confirmText: 'Revise',
                });
                if (ok) reviseMutation.mutate(current.id);
              }}
              disabled={busy}
              className="min-h-[40px]"
            >
              <FontAwesomeIcon icon={faRotate} className="mr-2 h-3.5 w-3.5" />
              Revise Quotation
            </Button>
          </div>
        )}

        {/* ── Append-only revision history — every revision stays visible ── */}
        {thread.revisions.length > 0 && (
          <div>
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Revision history
            </h4>
            <ul data-testid="quotation-revision-history" className="space-y-2">
              {[...thread.revisions]
                .sort((a, b) => b.revisionNumber - a.revisionNumber)
                .map((r) => (
                  <RevisionRow
                    key={r.id}
                    revision={r}
                    isCurrent={current?.id === r.id}
                    revisions={thread.revisions}
                  />
                ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
